import React from 'react';
import { TextAreaInput, useMobile } from '@octano/global-ui';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../locales/translations';
import { useValidations } from '../../../hooks/useValidations';
import { QuestionType, ScaleOption } from '../types';

const TEXT_SIZE = 250;

export type QuestionProps = {
  question: {
    id: string;
    text: string;
    type: QuestionType;
    evaluationScale?: { scaleOptions: ScaleOption[] } | null;
  };
  index: number;
};

const Question: React.FC<QuestionProps> = ({ question, index }) => {
  const {
    register,
    getValues,
    control,
    watch,
    formState: { errors },
  } = useFormContext();

  const { t } = useTranslation(TranslationsKeys.SURVEY);
  const { validateMaxLength } = useValidations();

  const isMobile = useMobile();
  const fieldName = `responses.${question.id}`;
  const currentValue = getValues(fieldName);
  const hasError = errors.responses?.[question.id];
  const radioStyle = isMobile
    ? { marginRight: 8, height: 15, width: 15 }
    : { marginRight: 8, height: 20, width: 20 };

  const openAnswer = watch(fieldName);

  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          fontSize: 20,
          fontWeight: 'bold',
          marginBottom: 20,
        }}
      >
        {index + 1}. {question.text}
      </div>

      {question.type === QuestionType.SingleChoice &&
        question.evaluationScale?.scaleOptions.map((opt) => (
          <label
            key={opt.id}
            style={{
              display: 'flex',
              cursor: 'pointer',
              padding: '6px 0',
            }}
          >
            <input
              type="radio"
              value={opt.id}
              {...register(fieldName, {
                required: 'Selecciona una opción',
              })}
              style={radioStyle}
              checked={currentValue === opt.id}
            />
            <div style={{ fontSize: '16px', marginTop: -3 }}>
              {opt.description}
            </div>
          </label>
        ))}

      {question.type === QuestionType.MultipleChoice && (
        // desarrollar cuando se necesite
        <></>
      )}

      {question.type === QuestionType.Text && (
        <>
          {/* El wizard renderiza una pregunta por paso reusando este mismo nodo:
              sin key, el textarea conserva en el DOM el texto de la pregunta
              anterior aunque el valor del form ya sea vacío. */}
          <TextAreaInput
            key={question.id}
            name={fieldName}
            label={t(`openQuestion.label`)}
            placeholder={t(`openQuestion.placeholder`)}
            control={control}
            className="mb-1"
            maxLength={TEXT_SIZE}
            rules={{
              required: t(`openQuestion.required`) as string,
              validate: {
                maxLength: validateMaxLength(TEXT_SIZE),
              },
            }}
          />
          <span>{`${openAnswer?.length || 0}/${TEXT_SIZE}`}</span>
        </>
      )}

      {/*
        Para las preguntas abiertas el propio TextAreaInput ya renderiza el
        mensaje de error bajo el campo, por lo que aquí sólo se muestra el
        error de las preguntas cerradas (los radios no tienen UI de error).
      */}
      {hasError && question.type !== QuestionType.Text && (
        <div style={{ color: 'red', fontSize: 14, marginTop: 8 }}>
          {hasError.message?.toString()}
        </div>
      )}
    </div>
  );
};

export default Question;
